import React, { useCallback, useEffect, useState } from "react";

import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";
import { Dropdown, LoadingSpinner } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import RadioGroup from "@app/component/input/RadioGroup";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useFrequencyIntervalOptions from "@app/util/frequency/useFrequencyIntervalOptions";

import FrequencyEntity from "../type/FrequencyEntity";

import CustomFrequencySelector from "./CustomFrequencySelector";
import styles from "./FrequencySelector.module.scss";

const NONE_VALUE = -1;

interface FrequencySelectorProps {
  crmActivityTypeIds?: Cadenced["crmActivityTypeId"];
  disabled?: boolean;
  entity?: FrequencyEntity;
  frequencyIntervalValue?: Cadenced["cadenceInterval"];
  isGroup?: boolean;
  onChange: (cadence: Partial<Cadenced>) => void;
  savedFrequencyInterval?: Cadenced["cadenceInterval"];
  showActivityTypes?: boolean;
  updating?: boolean;
}

const FrequencySelector = ({
  crmActivityTypeIds,
  disabled,
  entity,
  frequencyIntervalValue,
  isGroup,
  onChange,
  savedFrequencyInterval,
  showActivityTypes,
  updating,
}: FrequencySelectorProps) => {
  const intl = useIntl();

  const [customSelectorVisible, setCustomSelectorVisibility] = useState<boolean>(false);
  const analytics = useAnalytics();
  const [selectedNumber, setSelectedNumber] = useState<number>(0);

  useEffect(() => {
    setSelectedNumber(isNotEmpty(frequencyIntervalValue) ? frequencyIntervalValue : 0);
  }, [frequencyIntervalValue]);

  const handleHideCustomSelector = useCallback(() => {
    setCustomSelectorVisibility(false);
  }, [setCustomSelectorVisibility]);

  const options = useFrequencyIntervalOptions(savedFrequencyInterval, frequencyIntervalValue);

  const handleChange = useCallback(
    (cadence: number) => {
      onChange({ cadenceInterval: cadence === NONE_VALUE ? null : cadence });
      analytics.clicked(["Selected Interval"], { value: cadence === NONE_VALUE ? null : cadence });
      setSelectedNumber(cadence);
    },
    [analytics, onChange, setSelectedNumber]
  );

  return (
    <div className={styles.container}>
      <div className={styles.variants}>
        <RadioGroup<number>
          buttonStyle="solid"
          disabled={disabled}
          onChange={handleChange}
          value={frequencyIntervalValue === null ? NONE_VALUE : frequencyIntervalValue}
        >
          <Radio.Button className={styles.option} key="no-cadence" value={NONE_VALUE}>
            {selectedNumber === NONE_VALUE && updating ? (
              <LoadingSpinner className={styles.loader} micro />
            ) : (
              intl.formatMessage({
                id: "frequency.cadenceSelector.none",
                defaultMessage: "None",
                description: "frequency component - cadence selector - none text",
              })
            )}
          </Radio.Button>
          {options.map((number) => (
            <Radio.Button className={styles.option} key={number} value={number}>
              {selectedNumber === number && updating ? (
                <LoadingSpinner className={styles.loader} micro />
              ) : (
                number
              )}
            </Radio.Button>
          ))}
        </RadioGroup>
        <div className={styles.customContainer}>
          <Dropdown
            destroyPopupOnHide
            disabled={disabled}
            dropdownRender={() => (
              <CustomFrequencySelector
                cadenceInterval={selectedNumber !== NONE_VALUE ? selectedNumber : 0}
                crmActivityTypeIds={crmActivityTypeIds}
                entity={entity}
                isGroup={isGroup}
                onHide={handleHideCustomSelector}
                onUpdate={onChange}
                showActivityTypes={showActivityTypes}
              />
            )}
            onOpenChange={setCustomSelectorVisibility}
            open={customSelectorVisible}
            placement="bottomLeft"
            trigger={["click"]}
          >
            <ButtonLink className={styles.customInterval} disabled={disabled}>
              {intl.formatMessage({
                id: "frequency.cadenceSelector.custom",
                defaultMessage: "Custom",
                description: "frequency component - cadence selector - Custom text",
              })}
            </ButtonLink>
          </Dropdown>
        </div>
      </div>
      <div className={styles.intervalInDays}>
        {intl.formatMessage({
          id: "frequency.cadenceSelector.intervalInDays",
          defaultMessage: "Interval in days",
          description: "frequency component - cadence selector - interval in days",
        })}
      </div>
    </div>
  );
};

export default FrequencySelector;
